//-- Copyright (c) 2021 TMT Insights Group, LLC
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { LoadingStatuses } from '@/constants/constants';
import axios from '@/_utils/axios';
import { Connector, FileManagerState, FileSource, Folder, PolarisApiResponse, SavedState } from './slice_types';
import { RootState } from '@/redux/store';

const initialState: FileManagerState = {
  data: [],
  currentPath: [],
  currentPathString: '',
  nextPages: [],
  fileSources: [],
  currentDepth: 0,
  connectors: {} as Record<string, Connector>,
  searchParams: {
    prefix_search: ''
  },
  locations: [],
  locationPointer: 0,
  hasLoadedSourcesData: false,
  pagination: {
    page_size: 25,
    total: 0
  },
  status: LoadingStatuses.fulfilled,
  folderLoading: false,
  isTransferQueueDrawerOpen: false,
  lastOpenedPendingAssetLoaction: null
};
let abortController = new AbortController();

// THUNKS
export const getFileSources = createAsyncThunk('fileManager/getFileSources', async () => {
  return axios.get(`/vfs/file_sources/`).then((res) => ({ data: res.data as PolarisApiResponse<FileSource[]> }));
});

export const getFolder = createAsyncThunk('fileManager/getFolder', async ({ path: pathArray, isRefresh }: any, thunkAPI) => {
  const path = pathArray.map(encodeURI).join('/') + '/';
  const { searchParams, folderLoading } = (thunkAPI.getState() as RootState).fileManager;
  const depth = (path.match(/\//g) || []).length - 1 || 0;

  if (folderLoading) {
    abortController.abort();
    abortController = new AbortController();
  }
  return axios
    .post(`/vfs/files/${path}`.replace(/#/g, '%23'), searchParams, {
      signal: abortController.signal
    })
    .then((res) => ({ path, depth, data: res.data as PolarisApiResponse<Folder>, pathArray, isRefresh }))
    .catch((error) => thunkAPI.rejectWithValue({ depth, error }));
});

export const getMoreFiles = createAsyncThunk('fileManager/getMoreFiles', async ({ depth }: any, thunkAPI) => {
  const { fileManager } = thunkAPI.getState() as RootState;
  const currentPath = fileManager.currentPath;
  const pathRequired = currentPath.slice(0, depth + 1);
  const nextPageToken = fileManager.nextPages[depth];
  if (!nextPageToken) return;
  let fullPath = '';
  if (pathRequired && Array.isArray(pathRequired) && pathRequired.length > 0) {
    fullPath += `${pathRequired.join('/')}`;
  }
  return axios
    .get(`/vfs/files/${fullPath}/`, {
      params: {
        page: nextPageToken
      },
      signal: abortController.signal
    })
    .then((res): { depth: number; data: Folder['items'] } => ({ depth, data: res.data }));
});

export const newFolder = createAsyncThunk('fileManager/newFolder', async (fileName: string, thunkAPI) => {
  const { fileManager } = thunkAPI.getState() as RootState;
  const currentPath = fileManager.currentPath;
  const src_location = currentPath[0];
  const src_path = currentPath.slice(1, currentPath.length).concat(fileName).join('/') + '/';
  return axios.post(`/vfs/file_action/new_folder/`, { src_location, src_path }).then((res) => res.data);
});

export const refreshFiles = createAsyncThunk('fileManager/refreshFiles', async (_, thunkAPI) => {
  const { fileManager } = thunkAPI.getState() as RootState;
  const path = fileManager.currentPath;
  if (path && Array.isArray(path) && path.length > 0) {
    return thunkAPI.dispatch(getFolder({ path, isRefresh: true }));
  }
});

const processLocations = (state: FileManagerState, path: string, savedState: SavedState) => {
  const tip = state.locations[state.locations.length - 1];

  if (!tip || tip.path !== path) {
    state.locations.push({ path, savedState });
  } else if (tip.path === path) {
    tip.savedState = { ...tip.savedState, ...savedState };
  }
};

const getLocation = (pointer: FileManagerState['locationPointer'], state: FileManagerState) => {
  const locations = state.locations;
  const adjustedPointer = locations.length + pointer - 1;

  if (adjustedPointer >= 0) {
    return locations[adjustedPointer];
  }
};

const trimEmptyLastLocationPart = (val: string, index: number, arr: any[]) => !(val === '' && index === arr.length - 1);

const filesManagerSlice = createSlice({
  name: 'fileManager',
  initialState,
  reducers: {
    resetFileManager: (state) => {
      return {
        ...initialState,
        fileSources: state.fileSources,
        lastOpenedPendingAssetLoaction: state.lastOpenedPendingAssetLoaction
      };
    },
    setFileSource: (state, { payload }) => {
      state.currentPath = [payload];
      state.searchParams.prefix_search = '';
      state.hasLoadedSourcesData = false;
      delete state.searchParams.prefix_search;
    },
    pushPath: (state, { payload }) => {
      const key = payload?.replace(/(^[/]+)|([/]+$)/g, '');
      state.currentPath = state.currentPath.concat(key);
      delete state.searchParams.prefix_search;
    },
    setPath: (state, { payload }) => {
      state.currentPath = payload;
      state.currentDepth = payload.length - 1;
    },
    setHomePath: (state) => {
      state.currentPath = [];
      state.lastOpenedPendingAssetLoaction = null;
      state.locations.push({
        path: '',
        savedState: {
          data: [],
          currentPath: [],
          currentDepth: 0,
          pagination: {
            total: 0
          },
          nextPages: []
        }
      });
    },
    goBackPath: (state) => {
      state.locationPointer = state.locationPointer - 1;
      const getLocationResults = getLocation(state.locationPointer, state);
      const savedState = getLocationResults?.savedState;
      const path = getLocationResults?.path;

      if (savedState !== undefined && path !== undefined) {
        state.data = savedState.data;
        state.pagination = savedState.pagination;
        state.currentDepth = savedState.currentDepth;
        state.nextPages = savedState.nextPages;
        state.currentPath = path.split('/').map(decodeURI).filter(trimEmptyLastLocationPart);
      }
    },
    goForwardPath: (state) => {
      if (state.locationPointer === 0) {
        return;
      }

      state.locationPointer = state.locationPointer + 1;
      const getLocationResults = getLocation(state.locationPointer, state);
      const savedState = getLocationResults?.savedState;
      const path = getLocationResults?.path;

      if (savedState !== undefined && path !== undefined) {
        state.data = savedState.data;
        state.pagination = savedState.pagination;
        state.currentDepth = savedState.currentDepth;
        state.nextPages = savedState.nextPages;
        state.currentPath = path.split('/').map(decodeURI).filter(trimEmptyLastLocationPart);
      }
    },
    clearForwardLocations: (state) => {
      const adjustedPointer = state.locations.length + state.locationPointer;
      state.locations = state.locations.slice(0, adjustedPointer);
      state.locationPointer = 0;
      state.searchParams.prefix_search = '';
    },
    setSearchParams: (state, { payload }) => {
      state.searchParams = payload;
    },
    clearSearchParams: (state) => {
      state.searchParams = {
        prefix_search: ''
      };
    },
    setTransferQueueDrawer: (state, { payload }) => {
      state.isTransferQueueDrawerOpen = payload;
    },
    setLastOpenedPendingAssetLocation: (state, { payload }) => {
      state.lastOpenedPendingAssetLoaction = payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getFileSources.pending, (state) => {
      state.status = LoadingStatuses.pending;
    });
    builder.addCase(getFileSources.fulfilled, (state, { payload: { data } }) => {
      const sources = (data as any).response?.data as FileSource[];
      state.fileSources = sources;
      sources.forEach((source: any) => {
        state.connectors[source.slug] = {
          is_read_only: source?.is_read_only
        } as any;
        (source.connectors as any).forEach((connector: any) => {
          (state.connectors as any)[source.slug as any][connector?.connector_type] = {
            allows_upload: connector?.allows_upload,
            allows_download: connector?.allows_download
          };
        });
      });
      state.status = LoadingStatuses.fulfilled;
    });
    builder.addCase(getFileSources.rejected, (state) => {
      state.status = LoadingStatuses.rejected;
      state.folderLoading = false;
    });
    builder.addCase(getFolder.pending, (state) => {
      state.folderLoading = true;
    });
    builder.addCase(getFolder.fulfilled, (state, { payload: { path, depth, data, pathArray, isRefresh } }) => {
      state.hasLoadedSourcesData = true;
      state.folderLoading = false;
      const items = (data as any).response.items;
      const pagination = (data as any).response.paging_info || {};
      state.data[depth] = items;
      state.pagination.total = pagination.count;

      if (pagination.next) {
        state.nextPages[depth] = pagination.next;
      } else {
        delete state.nextPages[depth];
      }

      if (!isRefresh) {
        state.currentPathString = path;
        state.currentPath = pathArray;
        state.lastOpenedPendingAssetLoaction = pathArray;
        state.currentDepth = depth;

        processLocations(state, path, {
          data: state.data,
          currentDepth: state.currentDepth,
          pagination: state.pagination,
          nextPages: state.nextPages
        });
      }
    });
    builder.addCase(getFolder.rejected, (state, { payload }: any) => {
      if (payload?.error?.name !== 'CanceledError') {
        state.folderLoading = false;
      }
      state.status = LoadingStatuses.rejected;
      if (payload?.depth !== undefined) {
        state.data[payload.depth] = [];
      }
      state.pagination.total = 0;
    });
    builder.addCase(getMoreFiles.pending, (state) => {
      state.folderLoading = true;
    });
    builder.addCase(getMoreFiles.rejected, (state) => {
      state.folderLoading = true;
    });
    builder.addCase(getMoreFiles.fulfilled, (state, { payload: { depth, data } }: any) => {
      const items: Folder['items'] = data.response.items;
      state.data[depth] = state.data[depth].concat(...(items as any));
      const pagination = data.response.paging_info || {};
      state.folderLoading = false;
      if (depth === state.currentDepth) {
        state.pagination.total += pagination.count || 0;
      }
      if (pagination.next) {
        state.nextPages[depth] = pagination.next;
      } else {
        delete state.nextPages[depth];
      }

      processLocations(state, state.currentPathString, {
        data: state.data,
        nextPages: state.nextPages
      } as any);
    });
  }
});

export const {
  resetFileManager,
  setFileSource,
  pushPath,
  setPath,
  setHomePath,
  goBackPath,
  goForwardPath,
  setSearchParams,
  clearSearchParams,
  clearForwardLocations,
  setTransferQueueDrawer,
  setLastOpenedPendingAssetLocation
} = filesManagerSlice.actions;

export default filesManagerSlice.reducer;

export const CLEAN_PATH_REGEX = /(^[/]+)|([/]+$)/g;
